import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useResolvedPath } from 'react-router-dom'
import { ListSubheader, Tab as MuiTab, Tabs as MuiTabs, useMediaQuery, useTheme } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'

import { Box, Button, Divider, Icons, Link, Menu, MenuItem, Typography } from '..'
import { useNavigationGuard } from '../../hooks'

export function NavTabs({
  tabs,
  groups,
  breakpoint = 'sm',
  mobileMenuButton = false,
  sx,
  children,
  tabProps,
  // These props are kept for backwards compatibility but no longer used
  // The global NavigationGuardProvider handles unsaved changes warnings
  hasUnsavedChanges = false,
  discardUnsavedChanges,
  submitUnsavedChanges,
  submissionStatus,
  setSubmissionStatus,
  ...props
}) {
  const theme = useTheme()
  const navigate = useNavigate()
  const navigationGuard = useNavigationGuard()
  const isDesktopView = useMediaQuery(theme.breakpoints.up(breakpoint))
  const { pathname } = useLocation()
  const flatTabs = groups ? groups.flatMap((g) => g.tabs) : tabs
  const [active, setActive] = useState(flatTabs.filter(({ to }) => pathname.includes(to)).slice(-1)[0])
  useEffect(() => {
    setActive(flatTabs.filter(({ to }) => pathname.includes(to)).slice(-1)[0])
  }, [pathname, flatTabs])

  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const attemptNavigation = (to) => {
    // Resolve relative paths to absolute paths
    // This is necessary because the navigation guard may navigate from a different context
    const absolutePath = to.startsWith('/') ? to : `${pathname.split('/').slice(0, -1).join('/')}/${to}`

    // Check global navigation guard for all navigation
    // This handles unsaved changes warnings via the NavigationGuardProvider
    const canNavigate = navigationGuard.checkNavigation(absolutePath)
    if (!canNavigate) {
      return
    }

    // If global guard allows, proceed with navigation
    navigate(to)
  }

  if (isDesktopView) {
    if (groups) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            justifyContent: 'flex-start',
            alignItems: 'flex-end',
            gap: 2,
            overflowX: 'auto',
            ...sx,
          }}
        >
          {groups.flatMap((group, i) => {
            const isActiveGroup = group.tabs.some(({ to }) => to === active?.to)
            return [
              i > 0 ? (
                <Divider
                  key={`divider-${i}`}
                  orientation="vertical"
                  flexItem
                  sx={{ flexGrow: 0, flexShrink: 0, width: 'auto', borderColor: 'grey.400', borderRightWidth: '2px' }}
                />
              ) : null,
              <Box
                key={group.title ?? `group-${i}`}
                sx={{ display: 'flex', flexDirection: 'column', flexGrow: 0, flexShrink: 0, width: 'max-content' }}
              >
                <Typography
                  variant="body2"
                  color={isActiveGroup ? 'primary.main' : 'text.primary'}
                  sx={{
                    minHeight: '1.5em',
                    px: 1,
                    textAlign: 'center',
                    fontWeight: 800,
                    fontSize: '1.1rem',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}
                >
                  {group.title ?? ' '}
                </Typography>
                <MuiTabs value={isActiveGroup ? active.to : false} onChange={(e, to) => setActive(to)} {...props}>
                  {group.tabs.map(({ text, label, to, testId }) => (
                    <MuiTab
                      value={to}
                      onClick={() => attemptNavigation(to)}
                      {...tabProps}
                      label={label}
                      to={to}
                      key={label}
                      text={text}
                      data-testid={testId}
                    />
                  ))}
                </MuiTabs>
              </Box>,
            ]
          })}
          {children}
        </Box>
      )
    }
    return (
      <div>
        <MuiTabs value={active?.to ?? false} onChange={(e, to) => setActive(to)} sx={sx} {...props}>
          {tabs.map(({ text, label, to, testId }) => (
            <MuiTab
              value={to}
              onClick={() => attemptNavigation(to)}
              {...tabProps}
              label={label}
              to={to}
              key={label}
              text={text}
              data-testid={testId}
            />
          ))}
          {children}
        </MuiTabs>
      </div>
    )
  }
  return (
    <>
      <Button
        size={mobileMenuButton ? 'large' : 'medium'}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        variant="contained"
        disableElevation
        onClick={(event) => setAnchorEl(event.currentTarget)}
        endIcon={mobileMenuButton ? null : <Icons.KeyboardArrowDown />}
        data-testid="mobile-nav-menu-button"
        style={{ marginBottom: '1em', marginTop: '0.5em' }}
      >
        {mobileMenuButton ? <MenuIcon /> : active?.text ?? active?.label ?? <MenuIcon />}
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        {groups
          ? groups.flatMap((group, i) => [
              group.title ? (
                <ListSubheader
                  key={`header-${group.title ?? i}`}
                  disableSticky
                  sx={{
                    fontWeight: 800,
                    fontSize: '1rem',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: 'text.primary',
                    lineHeight: '2.5em',
                  }}
                >
                  {group.title}
                </ListSubheader>
              ) : null,
              ...group.tabs.map(({ text, label, to, testId }) => (
                <MenuItem
                  component={Link}
                  onClick={() => {
                    setAnchorEl(null)
                    attemptNavigation(to)
                  }}
                  label={label}
                  to={to}
                  key={label}
                  data-testid={testId}
                >
                  {text ?? label}
                </MenuItem>
              )),
            ])
          : tabs.map(({ text, label, to, testId }) => (
              <MenuItem
                component={Link}
                onClick={() => {
                  setAnchorEl(null)
                  attemptNavigation(to)
                }}
                label={label}
                to={to}
                key={label}
                data-testid={testId}
              >
                {text ?? label}
              </MenuItem>
            ))}
        {children}
      </Menu>
    </>
  )
}
